import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Network from '../../../models/Network.js';

import FilterListElement from '../list_elements/FilterListElement.js';
import LoadingListElement from '../list_elements/LoadingListElement.js';
import ReportListElement from '../list_elements/ReportListElement.js';
import GraphApiMultimodal from '../graphics/GraphApiMultimodal.js';
import GraphApiMultimodalInteractive from '../graphics/GraphApiMultimodalInteractive.js';
import GraphVegaBasic from '../graphics/GraphVegaBasic.js';
import GraphVegaLiteBasic from '../graphics/GraphVegaLiteBasic.js';
import GraphVegaLiteApiBasic from '../graphics/GraphVegaLiteApiBasic.js';
import GraphReportsTS from '../graphics/GraphReportsTS.js';
import './Module.css';

const key = process.env.REACT_APP_CLIENT_ACCESS_KEY;
const URLS = require("../../../urls.js");

const StatusCodes = require('../../../status-codes/status-codes.js')
const ErrorCodes = require('../../../error-codes/error-codes.js')

// function that takes in the reports JSON and manipulates the 'report_type' field to only be of the primary types
const consolidatePrimaryReportType = (reportArray) => {
    // for each object in the jsonData array, convert the report_type field from a comma-separated string to an array of strings
    reportArray.forEach((report) => {
        const temp = report.report_type.split(','); // TODO: remove
        // console.log('temp:', temp);
        // iterate through temp and upon an instance of 'general-emergent', set report_type to 'general-emergent'
        temp.forEach((type) => {
            if (type === 'general-emergent') {
                report.report_type = 'Emergent';
            } else if (type === 'cancelled') {
                report.report_type = 'Cancelled';
            } else if (type === 'non-emergent') {
                report.report_type = 'Non-Emergent';
            } else if (type === 'non-acute') {
                report.report_type = 'Non-Acute';
            } else if (type === 'refusal') {
                report.report_type = 'Refusal';
            } else if (type === 'unknown') {
                report.report_type = 'Unknown';
            }
        })
    });

    return reportArray;
}

// function that takes in the reports JSON and adds a new field 'status' based on the 'report_marked_finished' and 'report_marked_read' fields
const addStatusField = (reportArray) => {
    // for each object in the jsonData array, add a new field 'status'
    // status will be 'Unread' if report_marked_read is false
    // status will be 'Read' if report_marked_finished is false and report_marked_read is true
    // status will be 'Finished' if report_marked_finished is true
    reportArray.forEach((report) => {
        if (report.report_marked_finished) {
            report.status = 'Finished';
        } else if (report.report_marked_read) {
            report.status = 'Read';
        } else {
            report.status = 'Unread';
        }
    });
    
    return reportArray;
}

const ReviewGraphsModule = ({user, isSmallScreen}) => {
    const navigate = useNavigate();
    
    const [jsonData, setJsonData] = useState(null);
    const [medicsData, setMedicsData] = useState(null);
    const [filterDict, setFilterDict] = useState(null);
    const [selectedIncident, setSelectedIncident] = useState(null);
    const [selectedReportData, setSelectedReportData] = useState(null);
    
    const graphParams = {
        width: 400,
        height: 400
    }

    // Function to handle report button click
    const handleReportButtonClick = (reportId) => {
        navigate('/report/' + reportId);
    };

    // Function to find the selected report data
    const getSelectedReportData = () => {
        if (!selectedIncident || !jsonData) return null;
        return jsonData.find(report => report.incident_number === selectedIncident);
    };

    // Function to get detailed report data from server
    const getOneReport = async (reportId) => {
        const url = URLS.GET_ONE_REPORT;
        const dict = {
            uid: user.uid,
            report_id: reportId,
            CLIENT_ACCESS_KEY: key
        };
        let responseData;

        await Network.post(url, dict, (response) => {
            responseData = response.data;
            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something
            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")
            } else {
                console.log("Unknown Error")
            }
        });
        return responseData;
    };

    // function that gets multiple reports from the server
    const getSeveralReportsFiltered = async (desiredNumber, reportOffset, filterDict) => {
        
        const url = URLS.GET_SEVERAL_REPORTS_FILTERED;
        const dict = {
            uid: user.uid,
            number_of_reports: desiredNumber,
            offset: reportOffset,
            filter_dict: filterDict,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {

            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something

            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")

            } else {
                console.log("Unknown Error")
            }
        });

        return responseData;
    };

    // function that gets all medics for the agency
    const getAllAgencyMedics = async (agencyId) => {
        const url = URLS.GET_MEDICS_FOR_USER;
        const dict = {
            uid: user.uid,
            agency_id: agencyId,
            CLIENT_ACCESS_KEY: key
        };

        let responseData;

        await Network.post(url, dict, (response) => {

            responseData = response.data;

            const extractedErrorCode = responseData.toString();
            const status = response.status

            if (Network.checkValidStatusAndResponse(response)) {
                // do something

            } else if (status == StatusCodes.INTERNAL_SERVER_ERROR) {
                console.log("Server Error")

            } else {
                console.log("Unknown Error")
            }
        });

        return responseData;
    };

    // get reports from the server
    useEffect(() => {
        // get the reports based on the current filterDict
        if (filterDict) {
            getSeveralReportsFiltered(1000, 0, filterDict) // max 1000 reports, no offset
                .then((data) => {
                    consolidatePrimaryReportType(data); // consolidate report types
                    addStatusField(data); // add status field
                    setJsonData(data); // TODO: add protection against API failure
                    console.log('Setting JSON data:', data);
                })
                .catch((error) => {
                    console.log('Error:', error);
                });
            }
    }, [filterDict]);

    // get all medics from the server
    useEffect(() => {
        getAllAgencyMedics(user.uid)
            .then((data) => {
                setMedicsData(data);
            })
            .catch((error) => {
                console.log('Error:', error);
            });
    }, []);

    // get detailed report data when incident is selected
    useEffect(() => {
        if (selectedIncident && getSelectedReportData()) {
            const reportId = getSelectedReportData().report_id;
            getOneReport(reportId)
                .then((data) => {
                    if (data && data.length > 0) {
                        setSelectedReportData(data[0]);
                    }
                })
                .catch((error) => {
                    console.log('Error fetching detailed report data:', error);
                    setSelectedReportData(null);
                });
        } else {
            setSelectedReportData(null);
        }
    }, [selectedIncident, jsonData]);

    
    return (
        <div className={isSmallScreen ? "standard-module-screen-small" : "standard-module"}>
            <FilterListElement
                medicsData={medicsData}
                filterState={{
                    date: ["week"],
                    read: ["both"],
                    finished: ["both"]
                }}
                showClearButton={false}
                filterDropdownsVisible={{
                    medics: true,
                    read: true,
                    finished: true,
                    hasFeedback: true
                }}
                dictUpdateHandler={setFilterDict}
                // visible={false}
            />
            {selectedIncident && getSelectedReportData() && (
                <div style={{ marginTop: '20px' }}>
                    <ReportListElement
                        reportId={getSelectedReportData().report_id}
                        reportNumber={selectedReportData ? selectedReportData.pcr_number : getSelectedReportData().report_number}
                        incidentNumber={selectedReportData ? selectedReportData.incident_number : getSelectedReportData().incident_number}
                        numberOfIssues={selectedReportData ? selectedReportData.number_issues : getSelectedReportData().number_of_issues}
                        numberOfSuggestions={selectedReportData ? selectedReportData.number_suggestions : getSelectedReportData().number_of_suggestions}
                        reportType={selectedReportData ? selectedReportData.report_type : getSelectedReportData().report_type}
                        incidentTime={selectedReportData ? selectedReportData.unit_back_in_service : getSelectedReportData().incident_time}
                        read={selectedReportData ? selectedReportData.report_marked_read : getSelectedReportData().report_marked_read}
                        finished={selectedReportData ? selectedReportData.report_marked_finished : null}
                        narrativeSummarized={selectedReportData ? selectedReportData.narrative_summarized : null}
                        verbose={true}
                        onButtonClick={handleReportButtonClick}
                    />
                </div>
            )}
            {!selectedIncident && (
                <div>
                    <h4>Select a response see a report preview</h4>
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-around' }}>
                {jsonData ? (
                    <div>
                        <GraphApiMultimodalInteractive 
                            data={jsonData} 
                            graphParams={graphParams}
                            onIncidentSelect={setSelectedIncident}
                        />
                    </div>
                ) : (
                    <LoadingListElement height={100} backgroundColor="white"/>
                )}
            </div>
        </div>
    );
}

export default ReviewGraphsModule;